import React, { useContext, useEffect, useState } from 'react'
import { Elements } from '@stripe/react-stripe-js'
import clsx from 'clsx'
import { useTranslation } from 'react-i18next'

import { UserContext } from '../../../../contexts/UserContext'
import { useSettings } from '../../../../hooks/useSettings'
import {
  getPrimaryClass,
  getHoverClass,
  getBackgroundClass,
} from '../../../../utils/themeUtils'

import PaymentForm from './PaymentForm'

const Payment = ({
  orderDetails,
  setActiveMenu,
  stripePromise,
  clientSecret,
}) => {
  const { isDarkMode, currentTheme } = useSettings()
  const { user } = useContext(UserContext)
  const { t } = useTranslation('payments')

  const currencyCode = orderDetails.currency || user.currency
  const currency = currencyCode === 'eur' ? '€' : '$'
  // Prezzo del pacchetto calcolato server-side (centesimi). Fallback difensivo
  // per compatibilità se amountCents non fosse passato.
  const totalPrice =
    orderDetails.amountCents != null
      ? orderDetails.amountCents / 100
      : Number(orderDetails.credits) * Number(user.price_per_credit)

  return clientSecret && stripePromise ? (
    <div className="flex flex-col lg:flex-row gap-6 h-full">
      <div className="lg:w-[45%] flex flex-col justify-between">
        <div>
          <h2 className="text-2xl mb-4">{t('payment.orderDetails')}</h2>
          <div
            className={clsx(
              'p-3 mb-3 border-2 rounded-[10px] flex justify-between items-center text-sm',
              getPrimaryClass(currentTheme),
              'border-current'
            )}
          >
            <p>{t('payment.xpuHours')}</p>
            <p className="font-light">x{orderDetails.credits}</p>
            <p>
              <span className="mr-px">{currency}</span>
              {Number(totalPrice).toFixed(2)}
            </p>
          </div>
          <div className="flex justify-between items-center text-lg px-3">
            <p>{t('payment.total')}:</p>
            <p>
              <span className="mr-px">{currency}</span>
              {Number(totalPrice).toFixed(2)}
            </p>
          </div>
        </div>
        <button
          className={clsx(
            'h-[40px] w-full p-1 border-2 transition duration-100 rounded-xl mt-4',
            getPrimaryClass(currentTheme),
            'border-current',
            getHoverClass(currentTheme)
          )}
          onClick={() => setActiveMenu('Consumption')}
        >
          {t('payment.cancel')}
        </button>
      </div>
      <div className="lg:w-[55%] flex flex-col">
        <h2 className="text-2xl mb-4">{t('payment.paymentMethod')}</h2>
        <Elements
          stripe={stripePromise}
          options={{
            clientSecret,
            locale: 'en',
            appearance: {
              variables: {
                colorPrimary: isDarkMode ? '#8cb7ed' : '#648cb5',
                colorBackground: isDarkMode ? '#0E2E4C' : '#ffffff',
                colorText: isDarkMode ? '#ffffff' : '#0E2E4C',
                colorTextSecondary: isDarkMode ? '#ffffff' : '#0E2E4C',
                colorDanger: '#ff5252',
                borderRadius: '12px',
              },
              rules: {
                '.Input': {
                  paddingTop: '9px',
                  paddingBottom: '9px',
                  color: isDarkMode ? '#ffffff' : '#0E2E4C',
                  borderColor: isDarkMode ? '#416991' : '#416991',
                  backgroundColor: isDarkMode ? '#0F1A33' : '#41699130',
                  fontSize: '16px',
                  border: '2px solid',
                },
              },
            },
          }}
        >
          <PaymentForm setActiveMenu={setActiveMenu} />
        </Elements>
      </div>
    </div>
  ) : (
    <div className="flex flex-col items-center justify-center h-full gap-4">
      <img src="/assets/images/loader.svg" alt={t('payment.loading')} />
      <p className="text-sm opacity-70">{t('payment.loadingOptions')}</p>
    </div>
  )
}

export default Payment
